import { PERSONAS_DA_ALIANCA, PERSONAS_HUMANAS } from '@alianca/core/authz/personas';

import type { Persona } from '@/lib/hooks/useMe';

// Matriz rota → acesso (T07 §3.5/§5): fonte única do guard de UX. Quem decide o acesso é o backend (D01).
export const HOME = '/relatorios/associados';

const ALL: readonly Persona[] = PERSONAS_HUMANAS;
/** Configurações do sistema: só o Super-Admin. */
const SA: readonly Persona[] = ['super_admin'];
const ALIANCA: readonly Persona[] = PERSONAS_DA_ALIANCA;
const ALIANCA_AP: readonly Persona[] = [...PERSONAS_DA_ALIANCA, 'admin_parceiro'];
/** Só o administrador do parceiro (a própria empresa). */
const AP: readonly Persona[] = ['admin_parceiro'];

export type Access =
    | { publica: true }
    | { personas: readonly Persona[] };

/** Caminhos no formato do TanStack Router (`$param`). Redirects legados ficam no router. */
export const ROUTE_ACCESS = {
    '/login': { publica: true },
    '/acionar-sinistro': { publica: true },
    '/auth/callback': { publica: true },
    '/auth/complete-registration': { publica: true },
    '/auth/reset-password': { publica: true },
    /** O representante de parceiro novo: com o token do convite (antes do login) ou com a sessão em onboarding. */
    '/boas-vindas': { publica: true },
    '/manutencao': { publica: true },

    '/carteira': { personas: ALIANCA },
    '/carteira/$executivoId': { personas: ALIANCA },
    '/chargebacks': { personas: ALIANCA },
    '/chargebacks/$id': { personas: ALIANCA },
    '/comissoes': { personas: ALIANCA },
    '/comissoes/$tipo/$id': { personas: ALIANCA },
    '/configuracoes': { personas: SA },
    '/configuracoes/$secao': { personas: SA },
    '/configuracoes/contratos/$serviceType': { personas: SA },
    '/indicadores': { personas: ALIANCA },
    '/minha-empresa': { personas: AP },
    '/parceiros': { personas: ALIANCA },
    '/parceiros/$id': { personas: ALIANCA },
    '/parceiros/novo': { personas: ALIANCA },
    '/sinistros': { personas: ALIANCA },
    '/sinistros/$id': { personas: ALIANCA },
    '/solicitacoes': { personas: ALIANCA },
    '/solicitacoes/$id': { personas: ALIANCA },
    '/usuarios': { personas: ALIANCA_AP },
    '/usuarios/$id': { personas: ALIANCA_AP },

    '/contratos': { personas: ALL },
    '/contratos/$id': { personas: ALL },
    '/contratos/cotacao': { personas: ALL },
    '/contratos/novo': { personas: ALL },
    '/eletropostos/contratos/$id': { personas: ALL },
    '/eletropostos/contratos/novo': { personas: ALL },
    '/eletropostos/cotacao': { personas: ALL },
    '/eletropostos/propostas/$id': { personas: ALL },
    '/eletropostos/propostas/nova': { personas: ALL },
    '/propostas': { personas: ALL },
    '/propostas/$id': { personas: ALL },
    '/propostas/nova': { personas: ALL },
    '/relatorios/associados': { personas: ALL },
    '/relatorios/financeiro': { personas: ALL },
    '/relatorios/renovacoes': { personas: ALL },
    '/renovacoes': { personas: ALL },
    '/renovacoes/$id': { personas: ALL },
} as const satisfies Record<string, Access>;

export type RotaPagina = keyof typeof ROUTE_ACCESS;

/** Abertas com a manutenção ligada: a própria tela e o login (o Super-Admin entra para desligá-la). */
export const LIVRES_NA_MANUTENCAO: readonly string[] = ['/manutencao', '/login'];

/**
 * Redirects legados (custo zero, T07 D-F5): `/dashboard`, `/relatorios`; as listas de
 * eletropostos, unificadas nas de contratos e propostas com a aba de produto (docs/DESIGN-SYSTEM.md › Layout); e as
 * notificações de cobrança, que viraram uma seção de Configurações.
 */
export const REDIRECTS = {
    '/dashboard': HOME,
    '/relatorios': HOME,
    '/eletropostos/contratos': '/contratos?produto=eletroposto',
    '/eletropostos/propostas': '/propostas?produto=eletroposto',
    '/cobrancas/notificacoes': '/configuracoes/notificacoes',
} as const;

const PUBLICAS = Object.entries(ROUTE_ACCESS)
    .filter(([, a]) => 'publica' in a)
    .map(([p]) => p);

/** Página pública: o `api()` não redireciona para o /login nos códigos de sessão. */
export function isPublicRoute(pathname: string): boolean {
    return pathname === '/' || PUBLICAS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
